import type { Events, UserProfiles, VenueImages } from 'types/generated/client';
import {
  type DefaultActivityImageSource,
  getDefaultActivityImage,
} from 'utils/shared/getDefaultActivityImage';

// Optional + nullable: callers select different subsets, and one source is an API payload.
type Loose<T> = { [K in keyof T]?: T[K] | null };

type VenueImageFields = Loose<Pick<VenueImages, 'url' | 'path'>>;

// Record<> keeps organizers whose selection has no image fields assignable.
type OrganizerImageFields = Loose<
  Pick<UserProfiles, 'profileImageProviderUrl' | 'profileImageUrl'>
> &
  Record<string, unknown>;

interface ActivityImageSource extends Loose<Pick<Events, 'coverImageProviderUrl'>> {
  venue?: { images?: VenueImageFields[] | null } | null;
  ownerProfile?: OrganizerImageFields | null;
  hostUserProfile?: OrganizerImageFields | null;
}

// Shared fallback chain for event/lesson artwork: own cover → venue photo → organizer photo.
export const getActivityImageUrl = (source: ActivityImageSource): string | null => {
  if (source.coverImageProviderUrl) {
    return source.coverImageProviderUrl;
  }

  const image = source.venue?.images?.[0];
  if (image?.url) {
    return image.url;
  }
  if (image?.path) {
    return image.path;
  }

  const organizer = source.ownerProfile || source.hostUserProfile;
  return organizer?.profileImageProviderUrl || organizer?.profileImageUrl || null;
};

export const getActivityImageUrlOrDefault = (
  source: ActivityImageSource & DefaultActivityImageSource,
): string => getActivityImageUrl(source) || getDefaultActivityImage(source);
